// We use the MUI TextField component because the FaroTextField component has some things
// that we don't need like buttons to save and has some styling that we don't need.
import { SphereButton } from "@components/common/sphere-button";
import { SphereLabel } from "@components/common/sphere-label";
import { useToast, neutral } from "@flat-ui";
// eslint-disable-next-line no-restricted-imports -- Needed to use the MUI TextField component
import { Box, TextField } from "@mui/material";
import {
  LogEventParams,
  useTrackEvent,
} from "@utils/track-event/use-track-event";
import { copyToClipboard } from "@utils/ui-utils";

interface Props {
  /** Text to show as title of the dialog */
  title: string;

  /** URL to share */
  url: string;

  /** Params to pass to send appropriate events when clicking on copy button */
  copyLogEventParams: LogEventParams;
}

/**
 * Renders a dialog to share a URL.
 * It has a text field that shows the URL and a button to copy it to the clipboard.
 */
export function SphereShareLink({
  title,
  url,
  copyLogEventParams,
}: Props): JSX.Element {
  const { openToast } = useToast();
  const { trackEvent } = useTrackEvent();

  /** Copies the URL to the clipboard */
  function copyUrl(): void {
    copyToClipboard(url);

    trackEvent(copyLogEventParams);

    openToast({
      title: "Link copied",
    });
  }

  return (
    <Box component="div">
      <SphereLabel title={title} />
      <Box
        component="div"
        sx={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <TextField
          value={url}
          size="small"
          sx={{
            width: "100%",
            "& .MuiInputBase-input": {
              height: "18px",
              color: neutral[800],
              fontSize: "14px",
              textOverflow: "ellipsis",
            },
            marginRight: "10px",
            "&:focus-visible": {
              outline: "none",
            },
            "& .MuiInputBase-root": {
              borderTopRightRadius: "0",
              borderBottomRightRadius: "0",
              "& .MuiOutlinedInput-notchedOutline": {
                border: `1px solid ${neutral[500]}`,
              },
            },
          }}
        />
        <SphereButton
          sx={{
            marginLeft: "-10px",
            borderTopLeftRadius: "0px",
            borderBottomLeftRadius: "0px",
            height: "37px",
            minWidth: "80px",
          }}
          onClick={copyUrl}
        >
          Copy
        </SphereButton>
      </Box>
    </Box>
  );
}
